import { useMemo } from 'react';

import { PortalBootResponse } from '@/lib/types';
import { useAppState } from '@/state/hooks/app.state.hook';
import styled from 'styled-components';

import {
  cssObjectToString,
  parseCssObject,
  scopeSelfOrDescendant,
  selfOrDescendant,
  type CssObject,
} from './cssObjectToString';

export const DEFAULT_MENU_BACKGROUND_COLOR = '#fff';
export const DEFAULT_MENU_TEXT_COLOR = '#000';
const TRANSPARENT = 'transparent';

type MenuTheme = PortalBootResponse['theme']['menu'] | undefined;

export const StyledMenuScope = styled.div<{
  $menu: MenuTheme;
  $customCss?: CssObject;
}>`
  ${selfOrDescendant('.menu-container')} {
    background: ${({ $menu }) => $menu?.backgroundColor?.colorOne || DEFAULT_MENU_BACKGROUND_COLOR};
  }

  ${selfOrDescendant('.menu-item')},
  ${selfOrDescendant('.menu-sub-item')} {
    background: ${TRANSPARENT};
    color: ${({ $menu }) => $menu?.textColor || DEFAULT_MENU_TEXT_COLOR};
    font-family: ${({ $menu }) => $menu?.navigationFont || 'inherit'};
  }

  ${selfOrDescendant('.menu-item-hover')} {
    background: ${({ $menu }) => $menu?.tabHoverColor?.colorOne || TRANSPARENT};
  }

  ${selfOrDescendant('.menu-item-active')} {
    background: ${({ $menu }) => $menu?.selectedTabColor || TRANSPARENT};
  }

  ${selfOrDescendant('.menu-close-button')} {
    color: ${({ $menu }) => $menu?.textColor || DEFAULT_MENU_TEXT_COLOR};
  }

  ${({ $customCss }) => cssObjectToString(scopeSelfOrDescendant($customCss))}
`;

export const useMenuScopeProps = (): {
  $menu: MenuTheme;
  $customCss: CssObject | undefined;
} => {
  const { initialBoot } = useAppState();
  const menu = initialBoot?.theme?.menu;

  return {
    $menu: menu,
    $customCss: useMemo(() => parseCssObject(menu?.css), [menu?.css]),
  };
};
